/**
 * TotalCourseAndLivingCost — combined tuition + city living-cost card.
 *
 * Fetches /api/degrees/{id}/living-cost and shows a rounded all-in
 * figure for the full course length. Uses Student Budget MID as the
 * default lifestyle (the CityDetail page carries the adjustable
 * lifestyle slider). Falls back to a neutral "not available" strip
 * when the parent university has no `city_slug` mapped.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Home, MapPin, Wallet, ExternalLink, TrendingUp } from "lucide-react";
import AccommodationBlock from "./AccommodationBlock";

export default function TotalCourseAndLivingCost({ degreeId }) {
  const { format } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/degrees/${degreeId}/living-cost`);
        if (alive) setData(data);
      } catch {
        if (alive) setData({ available: false, reason: "error" });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [degreeId]);

  if (loading) {
    return (
      <div
        data-testid="total-course-living-cost-loading"
        className="mt-4 mb-6 rounded-lg border border-stone-200 bg-stone-50 p-5 animate-pulse"
      >
        <div className="h-3 w-40 bg-stone-200 rounded" />
        <div className="mt-3 h-6 w-64 bg-stone-200 rounded" />
      </div>
    );
  }

  if (!data || data.available === false) {
    return (
      <div
        data-testid="total-course-living-cost-empty"
        className="mt-4 mb-6 rounded-lg border border-dashed border-stone-300 bg-stone-50/60 p-4 text-xs text-stone-500"
      >
        <div className="flex items-start gap-2">
          <MapPin className="h-3.5 w-3.5 mt-0.5 text-stone-400" />
          <div>
            <div className="font-medium text-stone-700">
              Living-cost estimate unavailable
            </div>
            <div className="mt-0.5">
              {data?.message ||
                "We haven't mapped this university to a city guide yet."}{" "}
              <Link
                to="/cities"
                className="underline hover:text-[#4C1D95]"
                data-testid="cities-index-link-fallback"
              >
                Browse city guides
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { city, monthly_local, monthly_usd, tuition, totals } = data;
  const yrs = tuition?.duration_years || 3;

  const hasIntl = totals?.tuition_intl_usd != null;
  const hasHome =
    totals?.tuition_home_usd != null &&
    totals.tuition_home_usd !== totals.tuition_intl_usd;

  // Breakdown chips — pick the 4 most-illustrative line items in LOCAL currency.
  const chips = [
    {
      label: "Student housing",
      v: monthly_local?.rent_student_accommodation,
      icon: Home,
    },
    { label: "Groceries", v: monthly_local?.groceries_monthly },
    { label: "Transport", v: monthly_local?.monthly_transport },
    { label: "Utilities + wifi", v: monthly_local?.utilities_monthly },
  ].filter((c) => c.v && c.v > 0);

  const localFmt = (v) =>
    v == null
      ? "—"
      : new Intl.NumberFormat("en-GB", {
          style: "currency",
          currency: city.currency || "USD",
          maximumFractionDigits: 0,
        }).format(v);

  return (
    <div
      data-testid="total-course-living-cost"
      className="mt-4 mb-6 rounded-lg border border-stone-200 bg-white p-5 lg:p-6"
    >
      {/* Header */}
      <div className="flex items-start gap-2 mb-4">
        <Wallet className="h-4 w-4 text-[#4C1D95] mt-0.5" />
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#4C1D95]">
            Total course + living cost
          </div>
          <p className="mt-1 text-xs text-stone-500">
            Tuition × {yrs} yrs plus {city.name} monthly living cost × 12 ×{" "}
            {yrs}. Uses <span className="font-medium">Student Budget Mid</span>{" "}
            (~{localFmt(monthly_local.student_budget_mid)}/mo).{" "}
            <Link
              to={`/cities/${city.slug}`}
              className="underline hover:text-[#4C1D95]"
              data-testid="tclc-city-link"
            >
              Adjust lifestyle on the {city.name} page
            </Link>{" "}
            to model your own budget.
          </p>
        </div>
      </div>

      {/* Grand-total row (headline) */}
      <div className="grid sm:grid-cols-2 gap-4">
        {hasHome && (
          <TotalTile
            testid="tclc-home"
            label={`${yrs}-yr all-in · Home`}
            primary={format(totals.grand_total_home_mid_usd)}
            sub={`${format(totals.tuition_home_usd)} tuition + ${format(
              totals.living_mid_usd
            )} living`}
            band={`Frugal: ${format(totals.grand_total_home_low_usd)}`}
            tone="home"
          />
        )}
        <TotalTile
          testid="tclc-intl"
          label={`${yrs}-yr all-in · International`}
          primary={format(totals.grand_total_intl_mid_usd)}
          sub={`${format(totals.tuition_intl_usd)} tuition + ${format(
            totals.living_mid_usd
          )} living`}
          band={`Frugal: ${format(totals.grand_total_intl_low_usd)}`}
          tone="intl"
        />
      </div>

      {/* Monthly breakdown row */}
      {chips.length > 0 && (
        <div className="mt-5 pt-4 border-t border-stone-100">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">
              What the monthly {localFmt(monthly_local.student_budget_mid)}{" "}
              covers in {city.name}
            </div>
            <div className="text-[10px] text-stone-400 font-mono">
              ≈ {format(monthly_usd.student_budget_mid)}/mo
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {chips.map((c) => (
              <span
                key={c.label}
                data-testid={`tclc-chip-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-stone-50 px-2.5 py-1 text-[11px] text-stone-700"
              >
                {c.icon ? <c.icon className="h-3 w-3 text-stone-400" /> : null}
                <span>{c.label}</span>
                <span className="font-mono text-stone-500">
                  {localFmt(c.v)}
                </span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Feb 2026 · Task 5 — First-year accommodation summary (weekly
          rent + guarantee status). Silent for non-curated unis. */}
      <AccommodationBlock degreeId={degreeId} />

      {/* Footer — source + city link */}
      <div className="mt-4 pt-3 border-t border-stone-100 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[10px] text-stone-500">
          Living cost: {city.currency}, {city.as_of || "current"} · Source:{" "}
          {city.source_url ? (
            <a
              href={city.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[#4C1D95]"
              data-testid="tclc-source-link"
            >
              Numbeo <ExternalLink className="inline h-2.5 w-2.5" />
            </a>
          ) : (
            "curated"
          )}
        </div>
        <Link
          to={`/cities/${city.slug}`}
          data-testid="tclc-full-city-guide"
          className="inline-flex items-center gap-1 text-[11px] text-[#4C1D95] hover:underline font-medium"
        >
          <TrendingUp className="h-3 w-3" />
          Full {city.name} living-cost guide →
        </Link>
      </div>
    </div>
  );
}

function TotalTile({ label, primary, sub, band, testid, tone }) {
  const toneCls =
    tone === "home"
      ? "border-emerald-200 bg-emerald-50/40"
      : "border-stone-200 bg-stone-50/40";
  return (
    <div
      data-testid={testid}
      className={`rounded-md border ${toneCls} px-4 py-3.5`}
    >
      <div className="label-uppercase !text-[10px] text-stone-500">{label}</div>
      <div
        className="font-chip text-2xl font-semibold mt-1 text-[#002147]"
        data-testid={`${testid}-value`}
      >
        {primary}
      </div>
      <div className="mt-1 text-[11px] text-stone-500">{sub}</div>
      {band && (
        <div className="mt-0.5 text-[10px] text-stone-400 font-mono">
          {band}
        </div>
      )}
    </div>
  );
}
